import { useRef, useState } from "react";

import { useNavigation } from "@luce/core/adapters/navigation";
import { getErrorMessage } from "@luce/core/apollo";
import { useAuth } from "@luce/core/auth";
import { toast } from "@luce/ui";

import { useClientId, useClientProfile, useCountdown } from "../../shared";
import { BACK_TO } from "../../shared/lib";
import { useClientConfirmPhoneNumber } from "../gqlhooks/use-client-confirm-phone-number";
import { useClientVerifyPhoneNumber } from "../gqlhooks/use-client-verify-phone-number";
import { useDeleteClient } from "../gqlhooks/use-delete-client";
import {
  isPinCodeStillActive,
  OTP_CODE_LIFETIME_SECONDS,
  otpSecondsLeft,
  withLeadingPlus,
} from "./lib";
import type { DeleteAccountStep } from "./types";

export function useDeleteAccount() {
  const clientId = useClientId();
  const { signOut } = useAuth();
  const { replace } = useNavigation();
  const { client } = useClientProfile();
  const phoneNumber = client?.user?.phoneNumber ?? "";

  const [step, setStep] = useState<DeleteAccountStep>("idle");
  const [pinCode, setPinCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [agreed, setAgreed] = useState(false);
  const { remaining, restart } = useCountdown(OTP_CODE_LIFETIME_SECONDS);
  // The code's expiry, kept through close and reopen: a "still active" answer
  // carries none, so reopening reuses it
  const knownExpiry = useRef<{ phone: string; at: number } | null>(null);

  const [verifyPhoneNumber, { loading: isSendingOtp }] = useClientVerifyPhoneNumber();
  const [confirmPhoneNumber, { loading: isConfirmingOtp }] = useClientConfirmPhoneNumber();
  const [deleteClient, { loading: isDeleting }] = useDeleteClient();

  const phone = withLeadingPlus(phoneNumber);

  /** Seconds the new code has: the server's expiry, else its full lifetime. */
  const rememberExpiry = (expiresAt: string | undefined) => {
    const seconds = otpSecondsLeft(expiresAt) ?? OTP_CODE_LIFETIME_SECONDS;
    knownExpiry.current = { phone, at: Date.now() + seconds * 1000 };
    return seconds;
  };

  const knownSecondsLeft = () => {
    const known = knownExpiry.current;
    if (known?.phone !== phone) return undefined;
    const seconds = Math.ceil((known.at - Date.now()) / 1000);
    return seconds > 0 ? seconds : undefined;
  };

  const openOtp = (seconds?: number) => {
    setPinCode("");
    setOtpError("");
    setAgreed(false);
    restart(seconds);
    setStep("otp");
  };

  const start = async () => {
    try {
      const expiresAt = await verifyPhoneNumber(phone);
      openOtp(rememberExpiry(expiresAt));
    } catch (error) {
      const message = getErrorMessage(error, "Failed to send verification code");
      if (isPinCodeStillActive(message)) {
        openOtp(knownSecondsLeft());
        return;
      }
      toast.error(message);
    }
  };

  const resendOtp = async () => {
    try {
      const expiresAt = await verifyPhoneNumber(phone);
      restart(rememberExpiry(expiresAt));
      toast.success("Verification code resent");
    } catch (error) {
      const message = getErrorMessage(error, "Failed to resend verification code");
      if (isPinCodeStillActive(message)) {
        toast.info(message);
        return;
      }
      toast.error(message);
    }
  };

  const confirmOtp = async () => {
    setOtpError("");
    try {
      const confirmed = await confirmPhoneNumber(phone, pinCode);
      if (!confirmed) {
        setOtpError("Invalid verification code");
        return;
      }
      setStep("confirm");
    } catch (error) {
      setOtpError(getErrorMessage(error, "Invalid verification code"));
    }
  };

  const confirmDeletion = async () => {
    if (!agreed) {
      toast.info("Please confirm that you agree");
      return;
    }
    try {
      const deleted = await deleteClient(clientId);
      if (deleted) setStep("deleted");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete account"));
    }
  };

  const finish = () => {
    void signOut();
    replace(BACK_TO.home);
  };

  return {
    step,
    phoneNumber,
    pinCode,
    otpError,
    remaining,
    agreed,
    isSendingOtp,
    isConfirmingOtp,
    isDeleting,
    start,
    close: () => setStep("idle"),
    setPinCode,
    setAgreed,
    resendOtp,
    confirmOtp,
    confirmDeletion,
    finish,
  };
}
